// @flow
import type {
  CalendarURLAction,
  EventForm,
  EventFormRecurring,
  EventType,
  EventURLAction,
  FilterForm,
  FilterSettings
} from './calendar.types.js';

import type { DiscrepancyForm } from '../resource/resource.types.js';

import { getOption, type Options } from '../form/select.js';
import {
  ADD_PASSENGER,
  EDIT_PASSENGER,
  WEIGHT_AND_BALANCE
} from './weight-and-balance/weight-and-balance.consts';

export const dayHalf: number = 12 * 60 * 60 * 1000; // in milliseconds

export const ASC = 'ASC';
export const DSC = 'DSC';

export const DAY: CalendarURLAction = 'day';
export const WEEK: CalendarURLAction = 'week';
export const MONTH: CalendarURLAction = 'month';
export const REPORT: CalendarURLAction = 'report';
export const DISPATCH: CalendarURLAction = 'dispatch';
export const RESOURCE: CalendarURLAction = 'resource';

export const USERS_FILTER = 'USERS_FILTER';
export const USERS_FILTER_MODIFIED = 'USERS_FILTER_MODIFIED';

export const USERS_SETTINGS = 'USERS_SETTINGS';

export const CHECK_EVENT_PARAMS = 'CHECK_EVENT_PARAMS';

export const FULLSCREEN = 'FULLSCREEN';

export const SHOW_WAITING_LIST = 'SHOW_WAITING_LIST';

export const SLICE_BREADCRUMBS = 'SLICE_BREADCRUMBS';

export const GET_TIMEZONE = 'GET_TIMEZONE';

//--------- EVENT MANAGE ----------//
export const EVENT_ADD_TO_WAITING = 'EVENT_ADD_TO_WAITING';
export const EVENT_DELETE = 'EVENT_DELETE';
export const EVENT_SAVE = 'EVENT_SAVE';

//--------- EVENT TYPES ----------//
export const AIR_EVENT_TYPE: EventType = 'air';
export const MAINTENANCE_EVENT_TYPE: EventType = 'maintenance';

// ---------- CHECKOUT ---------- //
export const EVENT_CHECKOUT_START = 'EVENT_CHECKOUT_START';
export const CANCEL_CHECKOUT = 'CANCEL_CHECKOUT';
export const SAVE_COUNTER = 'SAVE_COUNTER';

// ---------- CHECKIN ---------- //
export const EVENT_CHECKIN_START = 'EVENT_CHECKIN_START';
export const CANCEL_CHECKIN = 'CANCEL_CHECKIN';

// ---------- DISCREPANCY ---------- //
export const DISCREPANCY_GET = 'DISCREPANCY_GET';
export const GET_AIRCRAFT = 'GET_AIRCRAFT';
export const CHANGE_STATUS = 'CHANGE_STATUS';
export const SAVE_DISCREPANCY = 'SAVE_DISCREPANCY';
export const DELETE_DISCREPANCY = 'DELETE_DISCREPANCY';
export const ADD_COMMENT = 'ADD_COMMENT';
export const EDIT_COMMENT = 'EDIT_COMMENT';

// ---------- MOVE EVENT ---------- //
export const MOVE_EVENT_START = 'MOVE_EVENT_START';
export const MOVE_EVENT_START_FAILED = 'MOVE_EVENT_START_FAILED';

export const MOVE_EVENT_FINISH = 'MOVE_EVENT_FINISH';
export const MOVE_EVENT_FINISH_FAILED = 'MOVE_EVENT_FINISH_FAILED';

export const MOVE_EVENT = 'MOVE_EVENT';
export const MOVE_EVENT_FAILED = 'MOVE_EVENT_FAILED';

export const MOVE_EVENT_RECURRENT = 'MOVE_EVENT_RECURRENT';

//--------- EVENT URL ACTIONS ----------//
export const EVENT_ADD: EventURLAction = 'event-add';
export const EVENT_VIEW: EventURLAction = 'event-view';
export const EVENT_EDIT: EventURLAction = 'event-edit';
export const EVENT_RESOURCE: EventURLAction = 'event-resource';
export const DISCREPANCY_ADD: EventURLAction = 'discrepancy-add';
export const DISCREPANCY_VIEW: EventURLAction = 'discrepancy-view';
export const EVENT_CHECKOUT: EventURLAction = 'event-checkout';
export const EVENT_CHECKIN: EventURLAction = 'event-checkin';
export const EVENT_CANCEL: EventURLAction = 'event-cancel';
export const EVENT_RECURRING: EventURLAction = 'event-recurring';
export const INSTRUCTORS_SCHEDULE: EventURLAction = 'instructors-schedule';

type ModalsHashParams = {
  // [key: EventURLAction]: $Keys<URLEventParams>,
  [EventURLAction]: $ReadOnlyArray<string>
};

export const modalsHashParams: ModalsHashParams = {
  [EVENT_ADD]: ['org', 'resource', 'startTime', 'finishTime', 'location', 'day'],
  [EVENT_VIEW]: ['event', 'resource', 'org', 'startTime', 'finishTime', 'location', 'day'],
  [EVENT_EDIT]: ['event', 'resource', 'org', 'startTime', 'finishTime', 'location', 'day'],
  [EVENT_RESOURCE]: ['event', 'org', 'startTime', 'finishTime', 'location', 'element', 'day'],
  [EVENT_CHECKOUT]: ['event', 'org', 'startTime', 'finishTime', 'day', 'element'],
  [EVENT_CHECKIN]: ['event', 'org', 'startTime', 'finishTime', 'element', 'day'],
  [EVENT_CANCEL]: ['event', 'org', 'startTime', 'finishTime', 'location', 'day'],
  [EVENT_RECURRING]: ['event', 'org', 'startTime', 'finishTime', 'location', 'resource', 'day'],
  [DISCREPANCY_VIEW]: [
    'event',
    'org',
    'startTime',
    'finishTime',
    'location',
    'discrepancyid',
    'element',
    'day'
  ],
  [DISCREPANCY_ADD]: ['event', 'org', 'startTime', 'finishTime', 'location', 'element', 'day'],
  [INSTRUCTORS_SCHEDULE]: [
    'event',
    'org',
    'startTime',
    'finishTime',
    'instructor',
    'orgId',
    'location',
    'day'
  ],
  [WEIGHT_AND_BALANCE]: ['event', 'resource', 'org', 'startTime', 'finishTime', 'location', 'day'],
  [ADD_PASSENGER]: ['event', 'org', 'startTime', 'finishTime', 'location', 'day', 'row', 'col'],
  [EDIT_PASSENGER]: ['event', 'org', 'startTime', 'finishTime', 'location', 'day', 'row', 'col']
};

type ModalsNames = {
  // [key: EventURLAction]: $Keys<URLEventParams>,
  [key: EventURLAction]: string | Object
};

export const modalsNames: ModalsNames = {
  [EVENT_ADD]: {
    [AIR_EVENT_TYPE]: 'New Regular Event',
    [MAINTENANCE_EVENT_TYPE]: 'New Maintenance Event',
    addToWaiting: 'New Waiting List Event'
  },
  [EVENT_VIEW]: {
    [AIR_EVENT_TYPE]: 'Regular Event',
    [MAINTENANCE_EVENT_TYPE]: 'Maintenance Event'
  },
  [EVENT_EDIT]: 'Edit',
  [EVENT_RESOURCE]: 'View Resource',
  [EVENT_CHECKIN]: 'Check In',
  [EVENT_CHECKOUT]: 'Check Out',
  [EVENT_CANCEL]: 'Cancel',
  [EVENT_RECURRING]: 'Recurring',
  [DISCREPANCY_VIEW]: 'View Discrepancy',
  [DISCREPANCY_ADD]: 'Add Discrepancy',
  [INSTRUCTORS_SCHEDULE]: "Instructor's Schedule",
  [WEIGHT_AND_BALANCE]: 'Manage passengers & Weight and Balance',
  [ADD_PASSENGER]: 'Add a passenger',
  [EDIT_PASSENGER]: 'Edit passenger'
};

export const FILTER_TOGGLER = {
  MAINTENANCE: 'maintenance',
  WAITING: 'waiting',
  MY_ONLY: 'myonly',
  ACTIVITY_COLOR_SCHEME: 'activityTypeColorScheme'
};

export const DEFAULT_FILTER = {
  organizations: [],
  locations: [],
  resources: [],
  instructors: [],
  [FILTER_TOGGLER.MAINTENANCE]: '1',
  [FILTER_TOGGLER.WAITING]: '1',
  [FILTER_TOGGLER.MY_ONLY]: '0',
  [FILTER_TOGGLER.ACTIVITY_COLOR_SCHEME]: '0',
  pilots: [],
  timezone: ''
};

export const defaultFilterForm: FilterForm = {
  id: '',
  name: 'Filter',
  ...DEFAULT_FILTER
};

export const defaultFilterSettings: FilterSettings = {
  futureDayLimit: '1',
  refreshTimeout: '0'
};

// ---------- CANCEL ---------- //
export const cancelReasonOptions: Options = [
  { label: 'Weather', value: 'weather' },
  { label: 'Mechanical', value: 'mechanical' },
  { label: 'Medical', value: 'medical' },
  { label: 'Force Majeure', value: 'forceMajeure' },
  { label: 'Maintenance', value: 'maintenance' },
  { label: 'Aircraft is Unavailable', value: 'aircraftIsUnavailable' },
  { label: 'Weather - New Regulations', value: 'weatherNewRegulations' },
  { label: 'Other', value: 'other' }
];

export const cancelActionOptions: Options = [
  { label: 'Current', value: 'current' },
  { label: 'Not started', value: 'not-started' },
  { label: 'Following', value: 'following' }
];

// ---------- RECURRING ---------- //
export const defaultEventFormRecurring: EventFormRecurring = {
  recurringType: 'day',
  recurring: '0',
  repsInterval: '1',
  repsType: 'number',
  reps: '1',
  recurringAction: 'add',
  recurringEndDate: '',
  daysOfWeek: ['5']
};

export const defaultEventForm: EventForm = {
  ...defaultEventFormRecurring,
  type: AIR_EVENT_TYPE,
  addToWaiting: '0',
  customFields: {},
  activityType: undefined,
  comments: '',
  resources: [],
  timezone: {}
};

export const chageRecurringOptions = [
  { label: 'Current (All other events in the series will remain the same)', value: 'current' },
  { label: 'Not Started (All the not started events will be changed)', value: 'not-started' },
  { label: 'Following (This and all the following events will be changed)', value: 'following' }
];

type Day = { short: string, full: string };

export const weekDayNames: $ReadOnlyArray<Day> = [
  {
    short: 'S',
    full: 'Sunday'
  },
  {
    short: 'M',
    full: 'Monday'
  },
  {
    short: 'T',
    full: 'Tuesday'
  },
  {
    short: 'W',
    full: 'Wednesday'
  },
  {
    short: 'T',
    full: 'Thursday'
  },
  {
    short: 'F',
    full: 'Friday'
  },
  {
    short: 'S',
    full: 'Saturday'
  }
];

// ---------- DISCREPANCY ---------- //
export const discrepancyStatuses = [
  { unique: 'all', name: 'All' },
  { unique: 'new', name: 'New' },
  { unique: 'open', name: 'Open' },
  { unique: 'inprogress', name: 'In Progress' },
  { unique: 'deferred', name: 'Deferred' },
  { unique: 'closed', name: 'Closed' }
];

export const statusNames = {
  new: 'New',
  open: 'Open',
  inprogress: 'In Progress',
  deferred: 'Deferred',
  closed: 'Closed'
};

export const discrepancyFields = {
  name: 'Name',
  assignTo: 'Assign To',
  status: 'Status',
  priority: 'Priority',
  description: 'Description',
  logbookEntry: 'Logbook Entry'
};

export const activityNames = {
  add: 'Added a Discrepancy',
  save: 'Changed Discrepancy',
  changeStatus: 'Changed Discrepancy',
  comment: 'Added a Comment'
};

export const statusOptions = discrepancyStatuses.slice(1).map(s => getOption(s.name, s.unique));

type Discrepancy = { name: string, className: string };

export const discrepancyPriorities: { [number]: Discrepancy } = {
  1: { name: 'High', className: 'text-red' },
  2: { name: 'Medium', className: 'text-orange' },
  3: { name: 'Low', className: 'text-green' }
};

export const priorityOptions = Object.keys(discrepancyPriorities).map(k =>
  getOption(discrepancyPriorities[k].name, k)
);

export const defaultDiscrepancyForm: DiscrepancyForm = {
  name: '',
  assignTo: '',
  priority: '3',
  description: '',
  disposition: '',
  status: '',
  authorName: '',
  authorSurname: '',
  logbookEntryCheck: '0',
  oldStatus: ''
};

export const endorsementsNames = {
  complex: 'Complex',
  performance: 'High Performance',
  highAltitudeOperations: 'High Altitude Operations',
  tailwheel: 'Tailwheel'
};

export const monthsOptions = [
  { label: 'January', value: '0' },
  { label: 'February', value: '1' },
  { label: 'March', value: '2' },
  { label: 'April', value: '3' },
  { label: 'May', value: '4' },
  { label: 'June', value: '5' },
  { label: 'July', value: '6' },
  { label: 'August', value: '7' },
  { label: 'September', value: '8' },
  { label: 'October', value: '9' },
  { label: 'November', value: '10' },
  { label: 'December', value: '11' }
];

export const DEFAULT_EVENT_COLOR = '#ececec';
